import React, { useEffect, useState, useCallback, useRef } from 'react';
import { leadsAPI } from '../lib/api';
import { Loader2, RefreshCw, Calendar, Mail, Phone, ClipboardList, HelpCircle, CheckCircle2, Bell, Brain, Sparkles, Clock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_ICONS = {
  schedule_demo: Calendar,
  send_email: Mail,
  call: Phone,
  create_task: ClipboardList,
  follow_up: Bell,
  qualify: HelpCircle,
  no_action: CheckCircle2,
};

// Star icon matching the onboarding
function StarIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M13.6027 1.12267C13.0575 -0.373899 10.9421 -0.373899 10.397 1.12267L7.92324 7.92324L1.12267 10.397C-0.373899 10.9421 -0.373899 13.0575 1.12267 13.6027L7.92324 16.0764L10.397 22.877C10.9421 24.3735 13.0575 24.3735 13.6027 22.877L16.0764 16.0764L22.877 13.6027C24.3735 13.0575 24.3735 10.9421 22.877 10.397L16.0764 7.92324L13.6027 1.12267Z" />
    </svg>
  );
}

function ActionBadge({ actionType, label, onClick }) {
  const Icon = ACTION_ICONS[actionType] || HelpCircle;
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border/70 bg-card hover:bg-muted/40 hover:border-border transition-all duration-150 shadow-sm cursor-pointer"
      data-testid={`action-badge-${actionType}`}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="text-xs font-medium text-foreground whitespace-nowrap">{label}</span>
    </button>
  );
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function LeadIntelligencePanel({ leadId, leadName, onAction }) {
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const intervalRef = useRef(null);

  const fetchLatest = useCallback(async () => {
    try {
      const [res, tlRes] = await Promise.all([
        leadsAPI.getLatestIntelligence(leadId),
        leadsAPI.getIntelligenceTimeline(leadId),
      ]);
      if (res.data?.has_data) {
        setData(res.data);
      }
      if (tlRes.data?.items) {
        setTimeline(tlRes.data.items);
      }
      setFetched(true);
    } catch {
      setFetched(true);
    }
  }, [leadId]);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leadsAPI.analyzeIntelligence(leadId);
      if (res.data) {
        setData(res.data);
        // Refresh timeline
        const tlRes = await leadsAPI.getIntelligenceTimeline(leadId);
        if (tlRes.data?.items) setTimeline(tlRes.data.items);
      }
    } catch { toast.error('Analysis failed'); }
    finally { setLoading(false); }
  }, [leadId]);

  const silentRefresh = useCallback(async () => {
    try {
      const check = await leadsAPI.hasNewActivity(leadId);
      if (check.data?.has_new) {
        const res = await leadsAPI.analyzeIntelligence(leadId);
        if (res.data) {
          setData(res.data);
          const tlRes = await leadsAPI.getIntelligenceTimeline(leadId);
          if (tlRes.data?.items) setTimeline(tlRes.data.items);
        }
      }
    } catch { /* silent */ }
  }, [leadId]);

  useEffect(() => {
    fetchLatest();
    intervalRef.current = setInterval(silentRefresh, 10 * 60 * 1000);
    return () => clearInterval(intervalRef.current);
  }, [fetchLatest, silentRefresh]);

  const actions = data?.actions?.length
    ? data.actions
    : data?.action && data.action !== 'no_action'
      ? [{ type: data.action, label: data.action_label }]
      : [];

  if (!fetched) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (fetched && !data) {
    return null;
  }

  const visibleTimeline = timelineExpanded ? timeline : timeline.slice(0, 3);

  return (
    <div className="space-y-6 py-4 px-1" data-testid="intelligence-panel">
      {/* ── AI Brain Analysis ── */}
      <div data-testid="ai-brain-analysis">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-sm shadow-cyan-500/20">
              <StarIcon className="h-3.5 w-3.5 text-white" />
            </div>
            <h3 className="text-sm font-semibold text-foreground">AI Brain Analysis</h3>
          </div>
          <button
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-1 px-2 rounded-md hover:bg-muted/50"
            onClick={runAnalysis}
            disabled={loading}
            data-testid="reanalyze-btn"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Re-analyze
          </button>
        </div>

        <div className="rounded-xl border border-border bg-card p-4" data-testid="intelligence-summary">
          <div className="space-y-1.5">
            {(data.summary || '').split(/[.!]\s+|—\s*|\n/).filter(s => s.trim().length > 8).slice(0, 5).map((sentence, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-1 h-1 rounded-full bg-cyan-400 mt-1.5 shrink-0" />
                <p className="text-sm leading-relaxed text-foreground/90">{sentence.trim().replace(/\.$/, '')}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {data.analyzed_at ? formatDate(data.analyzed_at) : ''}
          </p>
        </div>

        {actions.length > 0 && (
          <div className="mt-4" data-testid="recommended-action-section">
            <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Recommended Actions</p>
            <div className="flex flex-wrap gap-2" data-testid="action-badges-container">
              {actions.map((a, i) => (
                <ActionBadge
                  key={`${a.type}-${i}`}
                  actionType={a.type}
                  label={a.label}
                  onClick={() => onAction?.(a.type, a.label)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Brain Activity Timeline ── */}
      {timeline.length > 0 && (
        <div data-testid="brain-activity-timeline">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <h3 className="text-sm font-semibold text-foreground">Brain Activity Timeline</h3>
            <span className="text-xs text-muted-foreground">({timeline.length})</span>
          </div>

          <div className="space-y-0 relative" data-testid="timeline-items">
            {/* Vertical timeline line */}
            <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />

            {visibleTimeline.map((item, i) => (
              <div key={i} className="flex gap-3 py-2.5 relative" data-testid={`timeline-item-${i}`}>
                {/* Dot */}
                <div className={`w-[23px] h-[23px] rounded-full border-2 flex items-center justify-center shrink-0 z-10 ${
                  i === 0
                    ? 'border-cyan-400 bg-cyan-400/10'
                    : 'border-border bg-background'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-cyan-400' : 'bg-muted-foreground/30'}`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {(() => {
                    const points = (item.summary || '').split(/[.!]\s+|—\s*|\n/).map(s => s.trim()).filter(s => s.length > 8).slice(0, 3);
                    return points.length > 1 ? (
                      <div className="space-y-0.5">
                        {points.map((point, idx) => (
                          <div key={idx} className="flex items-start gap-1.5">
                            <div className="w-1 h-1 rounded-full bg-muted-foreground/40 mt-1.5 shrink-0" />
                            <p className="text-xs text-foreground/80 leading-relaxed">{point.replace(/\.$/, '')}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-foreground/80 line-clamp-2 leading-relaxed">{item.summary}</p>
                    );
                  })()}
                  {item.actions?.length > 0 && (
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {item.actions.slice(0, 3).map((a, j) => {
                        const Icon = ACTION_ICONS[a.type] || HelpCircle;
                        return (
                          <span key={j} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
                            <Icon className="h-2.5 w-2.5" />{a.label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground mt-1">{formatTimeAgo(item.analyzed_at)}</p>
                </div>
              </div>
            ))}
          </div>

          {timeline.length > 3 && (
            <button
              onClick={() => setTimelineExpanded(!timelineExpanded)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mt-1 ml-8"
              data-testid="timeline-expand-btn"
            >
              <ChevronRight className={`h-3 w-3 transition-transform ${timelineExpanded ? 'rotate-90' : ''}`} />
              {timelineExpanded ? 'Show less' : `Show ${timeline.length - 3} more`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
